import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { BasketService } from 'src/app/services/basket.service';
import { ApiResponse } from 'src/app/interfaces/api-response';
import { Basket, SelectedShippingCosts } from 'src/app/interfaces/basket';

@Component({
    selector: 'app-free-shipping-progress',
    templateUrl: './free-shipping-progress.component.html',
    styleUrls: ['./free-shipping-progress.component.scss'],
    standalone: false
})
export class FreeShippingProgressComponent implements OnInit, OnChanges {
  @Input() basketId: number | string;
  @Input() showDetails = true;
  @Input() total = 0;
  @Input() type: 'Parts' | 'Bicycles' = 'Parts';
  
  // Optional inputs to avoid API call when data is already available
  @Input() threshold: number = 0;
  @Input() shippingCostValue: number = 0;
  @Input() currencyCode: string = '';
  
  thresholdAmount: number = 0;
  shippingCost: number = 0;
  currency: string = 'EUR';
  progressPercentage: number = 0;
  remainingAmount: number = 0;
  isLoading: boolean = true;
  hasShippingThreshold: boolean = false;

  constructor(private basketService: BasketService) {}

  ngOnInit() {
    this.initializeData();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Re-calculate when inputs change
    if (changes['total'] || changes['threshold'] || changes['shippingCostValue'] || changes['currencyCode']) {
      if (this.hasPreloadedData()) {
        this.processPreloadedData();
      } else if (this.thresholdAmount > 0) {
        // Recalculate progress when total changes (even if data was loaded from API)
        this.updateProgress();
      }
    }
  }

  private updateProgress() {
    if (this.hasShippingThreshold) {
      this.progressPercentage = Math.min(
        (this.total / this.thresholdAmount) * 100,
        100
      );
      this.remainingAmount = Math.max(this.thresholdAmount - this.total, 0);
    }
  }

  private hasPreloadedData(): boolean {
    // Check if threshold data was provided via inputs
    return this.threshold > 0;
  }

  private initializeData() {
    // If threshold data is provided via inputs, use it directly (no API call)
    if (this.hasPreloadedData()) {
      this.processPreloadedData();
      return;
    }
    
    // Otherwise, fetch from API
    if (this.basketId) {
      this.loadShippingData();
    }
  }

  private processPreloadedData() {
    this.thresholdAmount = this.threshold;
    this.shippingCost = this.shippingCostValue;
    this.currency = this.currencyCode || 'EUR';
    this.hasShippingThreshold = this.thresholdAmount > 0;
    this.updateProgress();
    this.isLoading = false;
  }

  loadShippingData() {
    // If preloaded data is available, use it instead of API call
    if (this.hasPreloadedData()) {
      this.processPreloadedData();
      return;
    }

    this.isLoading = true;
    this.basketService.getShippingCostForBasket(this.basketId).subscribe(
      (response: ApiResponse) => {
      
        if (response && response.success && response.result) {
          const shippingCost = response.result as SelectedShippingCosts;
          this.processShippingData(shippingCost);
        }
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading shipping data:', error);
        this.isLoading = false;
      }
    );
  }

  processShippingData(shippingCost: SelectedShippingCosts) {
    // Find shipping cost details for the specified type (Parts or Bicycles)
    const shippingCostArray = shippingCost?.[this.type];

    if (shippingCostArray && shippingCostArray.length > 0) {
      const shippingDetails = shippingCostArray[0];

      this.thresholdAmount = shippingDetails.shipping_threshold?.threshold?.value || 0;
      this.shippingCost = Number(shippingDetails.shipping_costs?.value) || 0;
      this.currency = shippingDetails.shipping_costs?.currency || 'EUR';
      this.hasShippingThreshold = this.thresholdAmount > 0;
      this.updateProgress();
    }
  }

  get isFreeShipping(): boolean {
    return this.total >= this.thresholdAmount && this.hasShippingThreshold;
  }

  get showProgress(): boolean {
    return this.hasShippingThreshold && this.shippingCost > 0;
  }
}
